import {
  HeaderClassParams,
  ICellRendererParams,
  ValueFormatterFunc,
  ValueFormatterParams,
  ColDef,
} from "ag-grid-community";
import SearchGridSchema from "@/interfaces/SearchGridSchema";
import PillCellRenderer, {
  PillCellRendererProps,
} from "./Pill";
import { DefaultThreshold as DefaultThresholdType } from "@/interfaces/RiskThresholdTypes";
import { DefaultThresholdApi } from "@/api/RiskThreshold/DefaultThresholdApi";
import Logger from "@/utils/Logger";

// Threshold constants for risk interpretation
const FALLBACK_THRESHOLDS = {
  highMin: 0.0,
  highMax: 2.99,
  mediumMin: 3.0,
  mediumMax: 6.99,
  lowMin: 7.0,
  lowMax: 10.0,
} as const;

const getActiveThresholds = (): {
  highMin: number;
  highMax: number;
  mediumMin: number;
  mediumMax: number;
  lowMin: number;
  lowMax: number;
} => {
  try {
    const cached = DefaultThresholdApi.getCachedDefaults();
    if (cached) {
      // DefaultThreshold extends RiskThresholdRange and uses same property names
      const t = cached as unknown as DefaultThresholdType;
      return {
        highMin:
          typeof t.highMin === "number"
            ? t.highMin
            : FALLBACK_THRESHOLDS.highMin,
        highMax:
          typeof t.highMax === "number"
            ? t.highMax
            : FALLBACK_THRESHOLDS.highMax,
        mediumMin:
          typeof t.mediumMin === "number"
            ? t.mediumMin
            : FALLBACK_THRESHOLDS.mediumMin,
        mediumMax:
          typeof t.mediumMax === "number"
            ? t.mediumMax
            : FALLBACK_THRESHOLDS.mediumMax,
        lowMin:
          typeof t.lowMin === "number" ? t.lowMin : FALLBACK_THRESHOLDS.lowMin,
        lowMax:
          typeof t.lowMax === "number" ? t.lowMax : FALLBACK_THRESHOLDS.lowMax,
      };
    }
  } catch (e: unknown) {
    const errDetail =
      e instanceof Error ? `${e.name}: ${e.message}` : String(e);
    Logger.warn(
      `Failed to load active threshold defaults, falling back to built-in thresholds: ${errDetail}`,
      ["ColumnDefinitions.helpers"],
    );
  }

  return FALLBACK_THRESHOLDS;
};

/** Map a status string to a header class defined in index.css */
export const statusToHeaderClass = (status?: string): string => {
  switch (status) {
    case "HIGH":
      return "header-red-pill";
    case "MEDIUM":
      return "header-yellow-pill";
    case "LOW":
      return "header-green-pill";
    default:
      return "";
  }
};

/**
 * Map a status string to a pill class that styles data cells.
 */
export const statusToPillClass = (status?: string): string => {
  switch (status) {
    case "HIGH":
      return "pill-red";
    case "MEDIUM":
      return "pill-yellow";
    case "LOW":
      return "pill-green";
    default:
      return "pill-yellow"; // default neutral
  }
};

/**
 * Read a representative row from the grid API (first displayed row) and
 * extract the requested status field. Pure and testable.
 */
export const getRepresentativeStatus = (
  params: HeaderClassParams<SearchGridSchema, unknown>,
  statusField: keyof SearchGridSchema,
): string | undefined => {
  const firstRow = params?.api?.getDisplayedRowAtIndex?.(0) as
    | { data?: Partial<SearchGridSchema> }
    | undefined;

  const data = firstRow?.data;
  if (!data) return undefined;

  const value = (data as Record<string, unknown>)[statusField as string];
  return typeof value === "string" ? value : undefined;
};

/** Factory that creates a headerClass function for a given mapping between
 * a column and the status field that determines its color.
 */
export const headerClassFromStatusField = (
  statusField: keyof SearchGridSchema,
) => {
  return (
    params: HeaderClassParams<SearchGridSchema, unknown>,
  ): string | undefined => {
    const status = getRepresentativeStatus(params, statusField);
    return statusToHeaderClass(status) || undefined;
  };
};

/**
 * Create a small helper that returns a cellRendererParams object for pill renderer
 * based on a status field in the row data.
 */
export const pillParamsFromStatusField = (
  statusField: keyof SearchGridSchema,
) => {
  return (params: ICellRendererParams<SearchGridSchema, unknown>) => {
    const maybeStatus = params?.data
      ? (params.data as Record<string, unknown>)[statusField as string]
      : undefined;
    const statusStr = typeof maybeStatus === "string" ? maybeStatus : undefined;

    // Use status string as label if present; otherwise fallback to numeric formatting if value exists
    let text = "N/A";
    if (typeof maybeStatus === "string") {
      text = maybeStatus;
    } else if (typeof params?.value === "number") {
      // reuse formatter with a shaped object
      const vfParams = params as unknown as ValueFormatterParams<unknown>;
      text = formatScoreValue(vfParams);
    }

    return { pillClass: statusToPillClass(statusStr), text };
  };
};

/**
 * Format numeric score values consistently for the grid.
 * Returns formatted string or readable placeholder values.
 */
export const formatScoreValue: ValueFormatterFunc = (
  params: ValueFormatterParams<unknown>,
): string => {
  const raw = params?.value as unknown;
  if (!params?.data) return "";

  if (raw === 0) return "0.00";
  if (raw === null || Number.isNaN(Number(raw))) return "Not Available";

  const n = Number(raw);
  if (n < 0 || n > 10) return "Not Applicable";

  return n.toFixed(2);
};

/**
 * Formatter for private label flags. Uses explicit string checks.
 */
export const formatPrivateLabel: ValueFormatterFunc = (
  params: ValueFormatterParams<unknown>,
): string => {
  const raw = params?.value as unknown;
  if (!params?.data) return "";

  if (raw === "NK") return "N";
  if (raw === "KS") return "Y";
  if (raw === null) return "Not Available";

  return "Not Available";
};

/**
 * Determine pill color class based on a numeric score and threshold bounds.
 * Used for riskStatus and overallRiskScore columns that derive color from numeric ranges.
 */
export const getPillClassFromScore = (
  score: number | undefined | null,
): string => {
  const thresholds = getActiveThresholds();

  if (score === undefined || score === null)
    return statusToPillClass(undefined);

  if (score < thresholds.mediumMin) return statusToPillClass("HIGH");
  if (score > thresholds.mediumMax) return statusToPillClass("LOW");
  return statusToPillClass("MEDIUM");
};

/**
 * Determine display text based on a numeric score and threshold bounds.
 * Used for riskStatus column that shows "High", "Medium", "Low" text.
 */
export const getTextFromScore = (score: number | undefined | null): string => {
  const thresholds = getActiveThresholds();

  if (score === undefined || score === null) return "N/A";

  if (score < thresholds.mediumMin) return "High";
  if (score > thresholds.mediumMax) return "Low";
  return "Medium";
};

/**
 * Create cellRendererParams for score columns that derive color from a status field.
 * The text shows the numeric score, but the color comes from the associated risk status.
 */
export const createScorePillParamsFromStatus = (
  statusField: keyof SearchGridSchema,
) => {
  const perDimensionFields: Array<keyof SearchGridSchema> = [
    "transparencyRiskStatus",
    "environmentalRiskStatus",
    "humanRightsRiskStatus",
  ];

  return (
    params: ICellRendererParams<SearchGridSchema, unknown>,
  ): PillCellRendererProps => {
    // Guard: ensure we have row data before attempting to render
    if (!params || !params.data) {
      return { pillClass: "", text: "" };
    }

    const rawValue = params.value as number | undefined | null;

    // Handle missing numeric score
    if (rawValue === null || rawValue === undefined) {
      // No colored pill for missing values in per-dimension columns; keep text for visibility
      return { pillClass: "", text: "Not Available" };
    }

    // Ensure numeric range validity
    if (typeof rawValue !== "number" || rawValue < 0 || rawValue > 10) {
      return { pillClass: "", text: "Not Applicable" };
    }

    // Narrow status field value safely
    const statusCandidate = params.data[statusField];
    const statusStr =
      typeof statusCandidate === "string" ? statusCandidate : undefined;

    const isPerDimension = perDimensionFields.includes(statusField);

    // For per-dimension score columns: only force red when the associated status === 'HIGH'.
    // Otherwise, do not render colored pills (no yellow/green) - return empty pillClass.
    const pillClass = isPerDimension
      ? statusStr === "HIGH"
        ? statusToPillClass("HIGH")
        : ""
      : // Non per-dimension cases fall back to score-based color determination
        getPillClassFromScore(rawValue);

    return { pillClass, text: rawValue.toFixed(2) };
  };
};

/**
 * Create cellRendererParams for the riskStatus column.
 * Derives both text and color from overallRiskScore thresholds.
 */
export const createRiskStatusPillParams = (
  params: ICellRendererParams<SearchGridSchema, unknown>,
): PillCellRendererProps => {
  if (!params.data) {
    return { pillClass: "", text: "" };
  }

  const overallRiskScore = params.data.overallRiskScore;
  const text = getTextFromScore(overallRiskScore);
  const pillClass = getPillClassFromScore(overallRiskScore);

  return { pillClass, text };
};

/**
 * Create cellRendererParams for the overallRiskScore column.
 * Shows numeric score with color derived from score thresholds.
 */
export const createOverallRiskScorePillParams = (
  params: ICellRendererParams<SearchGridSchema, unknown>,
): PillCellRendererProps => {
  const score = params.value as number | undefined | null;

  if (!params.data) {
    return { pillClass: "", text: "" };
  }

  const formattedValue = formatScoreValue(
    params as unknown as ValueFormatterParams<unknown>,
  );
  const pillClass = getPillClassFromScore(score);

  return { pillClass, text: formattedValue };
};

/**
 * Create the risk-related column definitions (riskStatus and overallRiskScore).
 * These columns use scoreRiskStatus for header coloring and derive pill colors from
 * numeric score thresholds.
 */
export const createRiskColumnDefs = (): ColDef<SearchGridSchema>[] => {
  const riskStatusCol: ColDef<SearchGridSchema> = {
    field: "scoreRiskStatus",
    headerName: "Risk\nStatus",
    minWidth: 150,
    cellRenderer: PillCellRenderer,
    headerClass: headerClassFromStatusField("scoreRiskStatus"),
    cellRendererParams: createRiskStatusPillParams,
  };

  const overallRiskCol: ColDef<SearchGridSchema> = {
    field: "overallRiskScore",
    headerName: "Overall\nScore",
    minWidth: 150,
    filter: "agNumberColumnFilter",
    filterParams: {
      defaultOption: "inRange",
    },
    valueFormatter: formatScoreValue,
    cellRenderer: PillCellRenderer,
    headerClass: headerClassFromStatusField("scoreRiskStatus"),
    cellRendererParams: createOverallRiskScorePillParams,
  };

  return [riskStatusCol, overallRiskCol];
};
